// Staking tier types
export type LockDurationTier = "LUNAR" | "MARTIAN" | "STELLARIS" | "CELESTIAL";
export type AmountTier = "CADET" | "EXPLORER" | "COMMANDER" | "LEGEND";
export type StakingTier = `${LockDurationTier} ${AmountTier}`;

// Lock duration thresholds (in seconds) - determines suffix (CADET/EXPLORER/COMMANDER/LEGEND)
const LOCK_DURATION_THRESHOLDS = {
  CADET: 4838400, // 2 months (8 epochs) - 56 days
  EXPLORER: 13305600, // 5 months (22 epochs) - 154 days
  COMMANDER: 21772800, // 8 months (36 epochs) - 252 days
  LEGEND: 30844800, // 12 months (51 epochs) - 357 days
};

// Staking amount thresholds (in wei, assuming 18 decimals) - determines prefix (LUNAR/MARTIAN/STELLARIS/CELESTIAL)
const STAKING_AMOUNT_THRESHOLDS = {
  LUNAR: 500000 * 1e18, // 500,000 AEVO
  MARTIAN: 1500000 * 1e18, // 1,500,000 AEVO
  STELLARIS: 3000000 * 1e18, // 3,000,000 AEVO
  // CELESTIAL: 3,000,001+ (no upper bound)
};

// Vesting periods in weeks (based on tier matrix)
// Structure: [AmountTier (prefix)][LockDurationTier (suffix)]
const VESTING_MATRIX: Record<LockDurationTier, Record<AmountTier, number>> = {
  LUNAR: {
    CADET: 7,
    EXPLORER: 6,
    COMMANDER: 5,
    LEGEND: 4,
  },
  MARTIAN: {
    CADET: 6,
    EXPLORER: 5,
    COMMANDER: 4,
    LEGEND: 3,
  },
  STELLARIS: {
    CADET: 5,
    EXPLORER: 4,
    COMMANDER: 3,
    LEGEND: 2,
  },
  CELESTIAL: {
    CADET: 4,
    EXPLORER: 3,
    COMMANDER: 2,
    LEGEND: 1,
  },
};

// Staking weight multipliers (based on tier matrix)
// Structure: [AmountTier (prefix)][LockDurationTier (suffix)]
const STAKING_WEIGHTS: Record<LockDurationTier, Record<AmountTier, number>> = {
  LUNAR: {
    CADET: 1.0,
    EXPLORER: 1.4,
    COMMANDER: 2.0,
    LEGEND: 2.5,
  },
  MARTIAN: {
    CADET: 1.3,
    EXPLORER: 1.8,
    COMMANDER: 2.6,
    LEGEND: 3.2,
  },
  STELLARIS: {
    CADET: 1.5,
    EXPLORER: 2.1,
    COMMANDER: 3.0,
    LEGEND: 3.7,
  },
  CELESTIAL: {
    CADET: 1.7,
    EXPLORER: 2.4,
    COMMANDER: 3.4,
    LEGEND: 4.2,
  },
};

// Trading volume thresholds (in USD)
const TRADING_VOLUME_THRESHOLDS = {
  TIER_1: 500000, // $0 - $500,000
  TIER_2: 5000000, // $500,001 - $5,000,000
  TIER_3: 10000000, // $5,000,001 - $10,000,000
  // TIER_4: $10,000,001+ (no upper bound)
};

// Trading volume boost multipliers
const TRADING_BOOST_MULTIPLIERS: Record<number, number> = {
  1: 1, // Tier 1: 1x
  2: 2, // Tier 2: 2x
  3: 3, // Tier 3: 3x
  4: 4, // Tier 4: 4x
};

/**
 * Determines the lock duration tier (suffix) based on stake duration in seconds
 * Returns: CADET (2 months), EXPLORER (5 months), COMMANDER (8 months), LEGEND (12 months)
 */
export const getLockDurationTier = (
  stakeDurationSeconds: string | number
): AmountTier => {
  const duration = Number(stakeDurationSeconds);

  if (duration >= LOCK_DURATION_THRESHOLDS.LEGEND) {
    return "LEGEND";
  }
  if (duration >= LOCK_DURATION_THRESHOLDS.COMMANDER) {
    return "COMMANDER";
  }
  if (duration >= LOCK_DURATION_THRESHOLDS.EXPLORER) {
    return "EXPLORER";
  }
  return "CADET";
};

/**
 * Determines the amount tier (prefix) based on stake amount in wei
 * Returns: LUNAR (1-500k), MARTIAN (500k-1.5M), STELLARIS (1.5M-3M), CELESTIAL (3M+)
 */
export const getAmountTier = (
  stakeAmountWei: string | number
): LockDurationTier => {
  const amount = Number(stakeAmountWei);

  if (amount >= STAKING_AMOUNT_THRESHOLDS.STELLARIS) {
    return "CELESTIAL";
  }
  if (amount >= STAKING_AMOUNT_THRESHOLDS.MARTIAN) {
    return "STELLARIS";
  }
  if (amount >= STAKING_AMOUNT_THRESHOLDS.LUNAR) {
    return "MARTIAN";
  }
  return "LUNAR";
};

/**
 * Gets the complete staking tier based on lock duration and stake amount
 * Format: "{AmountTier} {LockDurationTier}" e.g., "LUNAR CADET", "CELESTIAL LEGEND"
 */
export const getStakingTier = (
  stakeDurationSeconds: string | number,
  stakeAmountWei: string | number
): StakingTier => {
  const amountTier = getAmountTier(stakeAmountWei); // Returns LUNAR/MARTIAN/STELLARIS/CELESTIAL
  const lockDurationTier = getLockDurationTier(stakeDurationSeconds); // Returns CADET/EXPLORER/COMMANDER/LEGEND
  return `${amountTier} ${lockDurationTier}` as StakingTier;
};

/**
 * Gets the vesting period in weeks for a given staking tier
 * @param amountTier - The amount tier (LUNAR/MARTIAN/STELLARIS/CELESTIAL) - determines prefix
 * @param lockDurationTier - The lock duration tier (CADET/EXPLORER/COMMANDER/LEGEND) - determines suffix
 */
export const getVestingPeriod = (
  amountTier: LockDurationTier,
  lockDurationTier: AmountTier
): number => VESTING_MATRIX[amountTier][lockDurationTier];

/**
 * Gets the staking weight multiplier for a given staking tier
 * @param amountTier - The amount tier (LUNAR/MARTIAN/STELLARIS/CELESTIAL) - determines prefix
 * @param lockDurationTier - The lock duration tier (CADET/EXPLORER/COMMANDER/LEGEND) - determines suffix
 */
export const getStakingMultiplier = (
  amountTier: LockDurationTier,
  lockDurationTier: AmountTier
): number => STAKING_WEIGHTS[amountTier][lockDurationTier];

/**
 * Determines the trading volume tier based on weekly trading volume in USD
 */
export const getTradingVolumeTier = (weeklyVolumeUSD: number): number => {
  if (weeklyVolumeUSD >= TRADING_VOLUME_THRESHOLDS.TIER_3) {
    return 4; // Tier 4: $10,000,001+
  }
  if (weeklyVolumeUSD >= TRADING_VOLUME_THRESHOLDS.TIER_2) {
    return 3; // Tier 3: $5,000,001 - $10,000,000
  }
  if (weeklyVolumeUSD >= TRADING_VOLUME_THRESHOLDS.TIER_1) {
    return 2; // Tier 2: $500,001 - $5,000,000
  }
  return 1; // Tier 1: $0 - $500,000
};

/**
 * Gets the trading volume boost multiplier for a given trading tier
 */
export const getTradingBoostMultiplier = (tradingTier: number): number =>
  TRADING_BOOST_MULTIPLIERS[tradingTier] || 1;

/**
 * Calculates the combined multiplier (staking multiplier * trading multiplier)
 */
export const getCombinedMultiplier = (
  stakingMultiplier: number,
  tradingMultiplier: number
): number => stakingMultiplier * tradingMultiplier;

/**
 * Base calculation for a staker's rewards metrics (without aggregate calculations)
 * Note: lockDurationTier stores the amount-based tier (LUNAR/MARTIAN/STELLARIS/CELESTIAL)
 *       amountTier stores the duration-based tier (CADET/EXPLORER/COMMANDER/LEGEND)
 *       This is for backwards compatibility with existing code
 */
export interface IStakerRewardsMetricsBase {
  accountAddress: string;
  stakeAmount: string; // Original stake amount in wei
  stakingTier: StakingTier;
  lockDurationTier: LockDurationTier; // Actually stores amount-based tier (LUNAR/MARTIAN/STELLARIS/CELESTIAL)
  amountTier: AmountTier; // Actually stores duration-based tier (CADET/EXPLORER/COMMANDER/LEGEND)
  vestingPeriod: number; // in weeks
  stakingMultiplier: number;
  tradeVolume: number; // Weekly volume in USD
  tradingTier: number;
  tradingMultiplier: number;
  combinedMultiplier: number;
}

/**
 * Complete calculation for a staker's rewards metrics (with aggregate calculations)
 */
export interface IStakerRewardsMetrics extends IStakerRewardsMetricsBase {
  stakeValue: number; // stakeAmount × combinedMultiplier (in wei)
  percentageShare: number; // stakeValue / totalStakeValue (as decimal, e.g., 0.01 = 1%)
  rewardAmount: number; // percentageShare × totalRewards (in token units)
}

export const calculateStakerRewardsMetrics = (
  accountAddress: string,
  stakeAmountWei: string | number,
  stakeDurationSeconds: string | number,
  weeklyTradeVolumeUSD: number
): IStakerRewardsMetricsBase => {
  const amountTier = getAmountTier(stakeAmountWei); // Returns LUNAR/MARTIAN/STELLARIS/CELESTIAL
  const lockDurationTier = getLockDurationTier(stakeDurationSeconds); // Returns CADET/EXPLORER/COMMANDER/LEGEND
  const stakingTier = getStakingTier(stakeDurationSeconds, stakeAmountWei);
  const vestingPeriod = getVestingPeriod(amountTier, lockDurationTier);
  const stakingMultiplier = getStakingMultiplier(amountTier, lockDurationTier);
  const tradingTier = getTradingVolumeTier(weeklyTradeVolumeUSD);
  const tradingMultiplier = getTradingBoostMultiplier(tradingTier);
  const combinedMultiplier = getCombinedMultiplier(
    stakingMultiplier,
    tradingMultiplier
  );

  return {
    accountAddress,
    stakeAmount: String(stakeAmountWei),
    stakingTier,
    lockDurationTier: amountTier, // Store amount tier in lockDurationTier field (backwards compatibility)
    amountTier: lockDurationTier, // Store lock duration tier in amountTier field (backwards compatibility)
    vestingPeriod,
    stakingMultiplier,
    tradeVolume: weeklyTradeVolumeUSD,
    tradingTier,
    tradingMultiplier,
    combinedMultiplier,
  };
};
